import { createContext, useContext, useEffect, useState } from 'react';

import styled from 'styled-components';

import { Alert } from '@components/alert';
import { Button } from '@components/button';
import { Form, useCreateFormStore } from '@components/form';
import { FormPasswordFieldProps } from '@components/form/form-password-field';
import { IconLock } from '@components/icons/lock';
import { Select } from '@components/select';
import { Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { TextStatusIndicator } from '@components/text-status-indicator';
import { useToaster } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import { useAuthenticationTest, useUpdateIntegrationAuthentication } from '../integrations-hooks';
import type {
  ApiKeyAuthentication,
  Authentication,
  AuthorizationHeaderAuthentication,
  BasicAuthentication,
  BearerTokenAuthentication,
  Integration,
  JwtTokenAuthentication,
  OAuth2Authentication,
} from '../integrations-types';

export type IntegrationProps = {
  integration: Integration;
};

type BaseUrlFormProps = {
  hasBaseUrl: boolean;
  baseUrl: string;
  formStore: ReturnType<typeof useCreateFormStore>;
};

type SelectableAuthType = Exclude<Authentication['authType'], 'oauth_connect'>;

const AuthTypeLabel: Record<SelectableAuthType, keyof (typeof locale)['en']> = {
  '': 'none',
  basic: 'basic',
  bearer_token: 'bearerToken',
  oauth2: 'oauth2',
  api_key: 'apiKey',
  authorization_header: 'authorizationHeader',
  jwt_token: 'jwtToken',
};

export const JWT_ALGORITHM_OPTIONS = [
  { label: 'HS256', value: 'HS256' },
  { label: 'RS256', value: 'RS256' },
];

export const useAuthTypeLabel = (authType: Authentication['authType'] = '') => {
  const { t } = useScopedTranslate(locale);
  const labelKey = authType === 'oauth_connect' ? 'oauth2' : authType;

  return t(AuthTypeLabel[labelKey]);
};

export const AuthenticationStatus = ({ integration }: IntegrationProps) => {
  const { t } = useScopedTranslate(locale);
  const isAuthenticated = integration?.authentication?.isAuthenticated;

  return isAuthenticated ? (
    <TextStatusIndicator text={t('connected')} variant="success" />
  ) : (
    <TextStatusIndicator text={t('notConnected')} variant="pending" />
  );
};

export const useAuthenticationSidebar = () =>
  useSidebar(AuthenticationSidebar, { variant: 'REGULAR' });

type AuthenticationProps<T = Authentication> = {
  authentication: T;
};

export const UrlField = ({
  id,
  label,
  hasBaseUrl,
  baseUrl,
  initialUrl,
  formStore,
  isRequired = true,
}: {
  id: 'url' | 'tokenUrl';
  label: string;
  initialUrl?: string;
  isRequired?: boolean;
} & BaseUrlFormProps) => {
  const { t } = useScopedTranslate(locale);
  const [showFullUrl, setShowFullUrl] = useState(
    !hasBaseUrl ||
      Boolean(initialUrl && /^https?:\/\//i.test(initialUrl) && !initialUrl.startsWith(baseUrl)),
  );

  const currentValue = ((formStore.useFieldValue(id) as string) ?? '').toString();

  return (
    <Stack space={2}>
      {showFullUrl ? (
        <Form.TextField
          key={`${id}-full-url`}
          id={id}
          label={label}
          validators={isRequired ? ['valid-http-url', 'required'] : ['valid-http-url']}
        />
      ) : (
        <Stack space={1}>
          <Form.TextField
            key={`${id}-path`}
            id={id}
            label={t('path')}
            validators={isRequired ? ['required'] : undefined}
          />
          <Text font="regular12" text={`${baseUrl}${currentValue}`} color="textHelper" />
        </Stack>
      )}
      {hasBaseUrl && (
        <Stack orientation="horizontal" align="center" space={1}>
          <Switch value={showFullUrl} onChange={setShowFullUrl} />
          <Text font="regular14" text={t('useFullUrl')} />
        </Stack>
      )}
    </Stack>
  );
};

const ExtraHeadersField = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <Form.Textarea
      id="extraHeaders"
      label={t('extraHeaders')}
      rows={2}
      validators={['json']}
      placeholder={t('secretUpdatePlaceholder')}
    />
  );
};

const PasswordField = (props: Pick<FormPasswordFieldProps<any>, 'label' | 'id'>) => {
  const { t } = useScopedTranslate(locale);
  const isEdit = useContext(IsEditContext);
  const placeholder = isEdit ? t('secretUpdatePlaceholder') : '';
  return (
    <Form.PasswordField
      autoComplete="new-password"
      placeholder={placeholder}
      hideVisibilityOnEmpty
      {...props}
    />
  );
};

const Advanced: React.FC = ({ children }) => {
  const { t } = useScopedTranslate(locale);
  const [isAdvanced, setIsAdvanced] = useState(false);

  return (
    <>
      <Switch label={t('toggleAdvanced')} onChange={() => setIsAdvanced(!isAdvanced)} />
      {isAdvanced && (
        <>
          {children}
          <ExtraHeadersField />
        </>
      )}
    </>
  );
};

const AuthorizationHeaderForm = ({
  authentication,
}: AuthenticationProps<AuthorizationHeaderAuthentication>) => {
  const { t } = useScopedTranslate(locale);

  return (
    <>
      <Form.TextField
        id="prefix"
        name="prefix"
        label={t('prefix')}
        defaultValue={authentication.details?.prefix}
      />
      <PasswordField id="token" label={t('token')} />
      <Advanced />
    </>
  );
};

const BearerTokenForm = ({
  authentication,
  hasBaseUrl,
  baseUrl,
  formStore,
}: AuthenticationProps<BearerTokenAuthentication> & BaseUrlFormProps) => {
  const { t } = useScopedTranslate(locale);
  const { details } = authentication;

  return (
    <>
      <UrlField
        id="url"
        label={t('authUrl')}
        initialUrl={details.url}
        hasBaseUrl={hasBaseUrl}
        baseUrl={baseUrl}
        formStore={formStore}
      />
      <Form.TextField
        id="clientId"
        label={t('clientId')}
        defaultValue={authentication.details.clientId}
      />
      <PasswordField id="clientSecret" label={t('clientSecret')} />
      <Advanced>
        <Form.Select
          id="requestType"
          label={t('requestType')}
          options={[
            { label: 'JSON', value: 'json' },
            { label: 'Multipart', value: 'multipart' },
            { label: 'Headers (GET)', value: 'headers' },
          ]}
          defaultValue={details.requestType ?? 'json'}
        />
        <Form.TextField
          id="clientIdKey"
          label={t('clientIdKey')}
          defaultValue={details.clientIdKey || 'client_id'}
        />
        <Form.TextField
          id="clientSecretKey"
          label={t('clientSecretKey')}
          defaultValue={details.clientSecretKey || 'client_secret'}
        />
        <Form.Textarea id="extraData" label={t('extraData')} rows={5} validators={['json']} />
        <Form.TextField id="prefix" label={t('prefix')} defaultValue={details.prefix ?? 'Bearer'} />
        <Form.TextField
          id="tokenLocation"
          label={t('tokenLocation')}
          defaultValue={details.tokenLocation}
        />
        <Form.TextField
          id="tokenHeader"
          label={t('tokenHeader')}
          defaultValue={details.tokenHeader || 'Authorization'}
        />
        <Form.Select
          id="tokenEncoding"
          label={t('tokenEncoding')}
          options={[
            { label: t('none'), value: 'none' },
            { label: t('base64'), value: 'base64' },
          ]}
          defaultValue={details.tokenEncoding ?? 'none'}
          isClearable={false}
        />
      </Advanced>
    </>
  );
};

const BasicForm = ({ authentication }: AuthenticationProps<BasicAuthentication>) => {
  const { t } = useScopedTranslate(locale);
  const { details } = authentication;

  return (
    <>
      <Form.TextField
        id="username"
        label={t('username')}
        defaultValue={details.username}
        validators={['required']}
      />
      <PasswordField id="password" label={t('password')} />
      <Advanced />
    </>
  );
};

const Oauth2Form = ({
  authentication,
  hasBaseUrl,
  baseUrl,
  formStore,
}: AuthenticationProps<OAuth2Authentication> & BaseUrlFormProps) => {
  const { t } = useScopedTranslate(locale);
  const { details } = authentication;

  return (
    <>
      <UrlField
        id="tokenUrl"
        label={t('tokenUrl')}
        initialUrl={details.tokenUrl}
        hasBaseUrl={hasBaseUrl}
        baseUrl={baseUrl}
        formStore={formStore}
      />
      <Form.TextField
        id="grantType"
        label={t('grantType')}
        defaultValue={details.grantType ?? 'authorization_code'}
      />
      <Form.TextField
        id="clientId"
        label={t('clientId')}
        defaultValue={details.clientId}
        validators={['required']}
      />
      <PasswordField id="clientSecret" label={t('clientSecret')} />
      <Advanced />
    </>
  );
};

const ApiKeyForm = ({ authentication }: AuthenticationProps<ApiKeyAuthentication>) => {
  const { t } = useScopedTranslate(locale);
  const { details } = authentication;

  return (
    <>
      <Form.TextField id="headerKey" label={t('headerKey')} defaultValue={details.headerKey} />
      <PasswordField id="apiKey" label={t('apiKey')} />
      <Advanced />
    </>
  );
};

const JwtTokenForm = ({
  authentication,
  hasBaseUrl,
  baseUrl,
  formStore,
}: AuthenticationProps<JwtTokenAuthentication> & BaseUrlFormProps) => {
  const { t } = useScopedTranslate(locale);
  const { details } = authentication;
  const defaultAlgorithm = details.algorithm ?? 'HS256';
  const currentAlgorithm = (formStore.useFieldValue('algorithm') as string) ?? defaultAlgorithm;
  const isRS256 = currentAlgorithm === 'RS256';
  const isEdit = useContext(IsEditContext);

  return (
    <>
      <Form.Select
        id="algorithm"
        label={t('algorithm')}
        options={JWT_ALGORITHM_OPTIONS}
        defaultValue={defaultAlgorithm}
        isClearable={false}
      />
      <Form.TextField
        id="issuer"
        label={t('issuer')}
        defaultValue={details.issuer}
        validators={['required']}
      />
      <Form.TextField
        id="key"
        label={t('jwtKey')}
        defaultValue={details.key}
        validators={['required']}
      />
      {isRS256 ? (
        <Form.Textarea
          id="secret"
          label={t('rsaPrivateKey')}
          rows={4}
          placeholder={isEdit ? t('secretUpdatePlaceholder') : ''}
        />
      ) : (
        <PasswordField id="secret" label={t('secret')} />
      )}
      <Advanced>
        <Form.TextField id="subject" label={t('subject')} defaultValue={details.subject} />
        <Form.TextField
          id="expirationSeconds"
          label={t('expirationSeconds')}
          defaultValue={details.expirationSeconds?.toString() ?? '30'}
          validators={['number']}
        />
        <Form.TextField id="prefix" label={t('prefix')} defaultValue={details.prefix ?? 'Bearer'} />
        <UrlField
          id="tokenUrl"
          label={t('jwtTokenUrl')}
          initialUrl={details.tokenUrl}
          hasBaseUrl={hasBaseUrl}
          baseUrl={baseUrl}
          formStore={formStore}
          isRequired={false}
        />
        <Form.TextField id="audience" label={t('audience')} defaultValue={details.audience} />
        <Form.TextField id="scope" label={t('scope')} defaultValue={details.scope} />
        <Form.TextField
          id="tokenLocation"
          label={t('tokenLocation')}
          defaultValue={details.tokenLocation}
        />
      </Advanced>
    </>
  );
};

const AuthComponent = ({
  authentication,
  hasBaseUrl,
  baseUrl,
  formStore,
}: AuthenticationProps & BaseUrlFormProps) => {
  switch (authentication.authType) {
    case 'authorization_header':
      return (
        <AuthorizationHeaderForm
          authentication={authentication as AuthorizationHeaderAuthentication}
        />
      );
    case 'bearer_token':
      return (
        <BearerTokenForm
          authentication={authentication as BearerTokenAuthentication}
          hasBaseUrl={hasBaseUrl}
          baseUrl={baseUrl}
          formStore={formStore}
        />
      );
    case 'basic':
      return <BasicForm authentication={authentication as BasicAuthentication} />;
    case 'oauth2':
      return (
        <Oauth2Form
          authentication={authentication as OAuth2Authentication}
          hasBaseUrl={hasBaseUrl}
          baseUrl={baseUrl}
          formStore={formStore}
        />
      );
    case 'api_key':
      return <ApiKeyForm authentication={authentication as ApiKeyAuthentication} />;
    case 'jwt_token':
      return (
        <JwtTokenForm
          authentication={authentication as JwtTokenAuthentication}
          hasBaseUrl={hasBaseUrl}
          baseUrl={baseUrl}
          formStore={formStore}
        />
      );
    default:
      return null;
  }
};

const IsEditContext = createContext(false);

const AuthenticationSidebar = ({ integration }: { integration: Integration }) => {
  const { t } = useScopedTranslate(locale);
  const [authType, setAuthType] = useState(integration.authentication?.authType ?? '');
  const isEdit = Boolean(integration.authentication?.authType === authType);
  const updateAuthenticationMutation = useUpdateIntegrationAuthentication(integration.shortId);
  const testMutation = useAuthenticationTest();
  const { showSuccess } = useToaster();
  const baseUrl = integration.baseUrl ?? '';
  const hasBaseUrl = Boolean(baseUrl);

  const initialValues: Authentication['details'] | {} = (() => {
    const auth = integration.authentication;
    if (!auth) return {};
    if (!hasBaseUrl) return auth.details;

    switch (auth.authType) {
      case 'oauth2': {
        const details = auth.details as OAuth2Authentication['details'];
        return { ...details, tokenUrl: (details.tokenUrl ?? '').replace(baseUrl, '') };
      }
      case 'bearer_token': {
        const details = auth.details as BearerTokenAuthentication['details'];
        return { ...details, url: (details.url ?? '').replace(baseUrl, '') };
      }
      case 'jwt_token': {
        const details = auth.details as JwtTokenAuthentication['details'];
        return { ...details, tokenUrl: (details.tokenUrl ?? '').replace(baseUrl, '') };
      }
      default:
        return auth.details;
    }
  })();
  const formStore = useCreateFormStore({ values: initialValues });

  const getAuthentication = () => {
    const details = { ...(formStore.getState().values as any) };
    if (authType === 'bearer_token') {
      const url = (details.url as string) ?? '';
      if (baseUrl && url && !url.startsWith('http')) {
        details.url = `${baseUrl}${url}`;
      }
    } else if (authType === 'oauth2' || authType === 'jwt_token') {
      const tokenUrl = (details.tokenUrl as string) ?? '';
      if (baseUrl && tokenUrl && !tokenUrl.startsWith('http')) {
        details.tokenUrl = `${baseUrl}${tokenUrl}`;
      }
    }
    return { authType, details } as Authentication;
  };

  const handleSave = async () => {
    await updateAuthenticationMutation.mutateAsync(getAuthentication());
    showSuccess(t('authUpdated'));
    hideSidebar();
  };

  const tokenUrl = formStore.useFieldValue('tokenUrl') as string | undefined;
  const testRequired =
    ['bearer_token', 'oauth2'].includes(authType) ||
    (authType === 'jwt_token' && Boolean(tokenUrl));
  const [testStatus, setTestStatus] = useState('');

  const isError = testMutation.isError || (testMutation.isSuccess && !testMutation?.data?.success);
  const isSuccess = testMutation.isSuccess && testMutation?.data.success;

  useEffect(() => {
    setTestStatus(isSuccess ? 'success' : isError ? 'error' : '');
  }, [isError, isSuccess]);

  const canSave = !testRequired || testStatus === 'success';

  return (
    <Sidebar>
      <IsEditContext.Provider value={isEdit}>
        <Sidebar.SimpleHeader title={t('header')} />
        <Form onSubmit={handleSave} store={formStore}>
          <Sidebar.Content>
            <Stack space={4}>
              <Select
                label={t('type')}
                options={Object.entries(AuthTypeLabel).map(([value, translationKey]) => ({
                  label: t(translationKey),
                  value,
                }))}
                defaultValue={authType}
                onChange={(value) => {
                  setTestStatus('');
                  setAuthType(value as Authentication['authType']);
                }}
              />
              <AuthComponent
                authentication={getAuthentication()}
                hasBaseUrl={hasBaseUrl}
                baseUrl={baseUrl}
                formStore={formStore}
              />
              {authType && testRequired && (
                <>
                  <Stack orientation="horizontal">
                    <Button
                      text={t('testAuth')}
                      onClick={() => testMutation.mutateAsync(getAuthentication())}
                      iconLeft={<IconLock />}
                      isDisabled={testMutation.isLoading}
                    />
                  </Stack>
                  {testStatus === 'success' && (
                    <Alert variant="success">
                      <Text text={t('authTestSuccess')} />
                    </Alert>
                  )}
                  {testStatus === 'error' && (
                    <Alert variant="error">
                      <CodeBlock>
                        {JSON.stringify(testMutation.data, null, 2) || t('generalError')}
                      </CodeBlock>
                    </Alert>
                  )}
                </>
              )}
            </Stack>
          </Sidebar.Content>
          <Sidebar.Footer>
            <Stack space={2} orientation="horizontal" justify="flex-end">
              <Form.Actions>
                <Button text={t('core.cancel')} variant="secondary" onClick={hideSidebar} />
                <Form.Submit text={t('core.save')} variant="primary-blue" disabled={!canSave} />
              </Form.Actions>
            </Stack>
          </Sidebar.Footer>
        </Form>
      </IsEditContext.Provider>
    </Sidebar>
  );
};

const locale = {
  en: {
    header: 'Authentication',
    type: 'Authentication type',
    toggleAdvanced: 'Advanced settings',
    extraData: 'Extra data (optional)',
    extraHeaders: 'Extra headers (optional)',
    requestType: 'Request type',
    prefix: 'Prefix (optional)',
    tokenUrl: 'Token URL',
    grantType: 'Grant type',
    authUrl: 'Authentication URL',
    clientId: 'Client ID',
    clientIdKey: 'Client ID key',
    clientSecret: 'Client secret',
    clientSecretKey: 'Client secret key',
    tokenLocation: 'Token location (data.token for {data: {token: XYZ}})',
    tokenHeader: 'Token header (default: Authorization)',
    tokenEncoding: 'Token encoding',
    base64: 'Base64',
    username: 'Username',
    password: 'Password',
    apiKey: 'API key',
    headerKey: 'Header key',
    token: 'Token',
    testAuth: 'Test',
    authTestSuccess: 'Authentication test successful',
    authUpdated: 'Authentication updated',
    generalError: 'Authorization returned an unspecified error',
    none: 'None',
    basic: 'Basic',
    bearerToken: 'Bearer token',
    oauth2: 'OAuth2',
    authorizationHeader: 'Authorization header',
    jwtToken: 'JWT Token',
    issuer: 'Issuer (API User ID)',
    jwtKey: 'Key',
    secret: 'Secret',
    rsaPrivateKey: 'RSA Private Key (PEM)',
    algorithm: 'Signing algorithm',
    jwtTokenUrl: 'Token URL (optional)',
    audience: 'Audience (optional)',
    scope: 'Scope (optional)',
    subject: 'Subject (optional)',
    expirationSeconds: 'Expiration seconds (default: 30)',
    connected: 'Connected',
    notConnected: 'Not connected',
    secretUpdatePlaceholder: 'Leave blank to keep current',
    path: 'Path',
    useFullUrl: 'Use full URL',
  },
};

const CodeBlock = styled.pre(() => ({
  'word-wrap': 'break-word',
  'white-space': 'pre-wrap',
}));
